import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'

const ADMIN_USER_ID = 'ae83677a-aaea-47be-a5df-3548cf90092f'

const CATEGORIES = [
  'Pitch Emails',
  'Cold Intro Emails',
  'Bio',
  'Outlines',
  'Social Media',
  'LinkedIn Profile',
  'Contracts',
  'Other',
]

// Seeded use counts by category — weighted by expected popularity
const SEED_COUNTS = {
  'Pitch Emails':     { min: 120, max: 247 },
  'Cold Intro Emails':{ min: 100, max: 220 },
  'Bio':              { min: 80,  max: 180 },
  'Outlines':         { min: 40,  max: 120 },
  'Social Media':     { min: 60,  max: 160 },
  'LinkedIn Profile': { min: 70,  max: 190 },
  'Contracts':        { min: 18,  max: 90  },
  'Other':            { min: 18,  max: 60  },
}

function seedCount(category) {
  const range = SEED_COUNTS[category] || { min: 18, max: 100 }
  return Math.floor(Math.random() * (range.max - range.min + 1)) + range.min
}

// Minimal toolbar for the admin editor
function EditorToolbar({ editor }) {
  if (!editor) return null
  const btn = (action, label, active) => (
    <button
      onMouseDown={e => { e.preventDefault(); action() }}
      style={{
        background: active ? '#334155' : 'transparent',
        border: '1px solid #334155',
        color: active ? '#f1c40f' : '#94a3b8',
        padding: '3px 8px', fontSize: 12, cursor: 'pointer',
        fontFamily: 'monospace',
      }}
    >{label}</button>
  )
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '8px', background: '#1e293b', borderBottom: '1px solid #334155' }}>
      {btn(() => editor.chain().focus().toggleBold().run(), 'B', editor.isActive('bold'))}
      {btn(() => editor.chain().focus().toggleItalic().run(), 'I', editor.isActive('italic'))}
      {btn(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2', editor.isActive('heading', { level: 2 }))}
      {btn(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3', editor.isActive('heading', { level: 3 }))}
      {btn(() => editor.chain().focus().toggleBulletList().run(), '• List', editor.isActive('bulletList'))}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), '1. List', editor.isActive('orderedList'))}
      {btn(() => {
        const url = window.prompt('URL')
        if (url) editor.chain().focus().setLink({ href: url }).run()
      }, 'Link', editor.isActive('link'))}
      {btn(() => editor.chain().focus().unsetLink().run(), 'Unlink', false)}
    </div>
  )
}

function TemplateEditor({ initialContent, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
    ],
    content: initialContent || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  return (
    <div style={{ border: '1px solid #334155', background: '#0f172a' }}>
      <EditorToolbar editor={editor} />
      <EditorContent
        editor={editor}
        style={{ minHeight: 200, padding: '12px 16px', color: '#e2e8f0', fontSize: 14, fontFamily: "'Nunito Sans', sans-serif" }}
      />
    </div>
  )
}

function TemplateForm({ initial, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || '')
  const [category, setCategory] = useState(initial?.category || CATEGORIES[0])
  const [content, setContent] = useState(initial?.content || '')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return
    setSaving(true)
    await onSave({ title: title.trim(), category, content })
    setSaving(false)
  }

  const inputStyle = {
    width: '100%', background: '#1e293b', border: '1px solid #334155',
    color: '#e2e8f0', padding: '9px 12px', fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: '#94a3b8', marginBottom: 5,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  return (
    <div style={{ background: '#1e293b', border: '1px solid #f1c40f44', padding: 24, marginBottom: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <div style={labelStyle}>Title *</div>
          <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Cold Pitch — SaaS Companies" />
        </div>
        <div>
          <div style={labelStyle}>Category *</div>
          <select style={inputStyle} value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>Content *</div>
        <TemplateEditor initialContent={initial?.content || ''} onChange={setContent} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSubmit} disabled={saving || !title.trim() || !content.trim()} style={{
          background: '#f1c40f', color: '#0a2342', border: 'none',
          padding: '9px 22px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", fontWeight: 700,
          cursor: title.trim() && content.trim() ? 'pointer' : 'not-allowed',
        }}>{saving ? 'Saving...' : (initial ? 'Update Template' : 'Add Template')}</button>
        <button onClick={onCancel} style={{
          background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
          padding: '9px 22px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", cursor: 'pointer',
        }}>Cancel</button>
      </div>
    </div>
  )
}

export default function Admin({ session }) {
  const [authorized, setAuthorized] = useState(false)
  const [activeSection, setActiveSection] = useState('templates')
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [filterCategory, setFilterCategory] = useState('All')
  const [search, setSearch] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    if (session?.user?.id === ADMIN_USER_ID) {
      setAuthorized(true)
      fetchTemplates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchTemplates = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('library_templates')
      .select('*')
      .order('created_at', { ascending: false })
    if (!error) setTemplates(data)
    setLoading(false)
  }

  const handleAdd = async ({ title, category, content }) => {
    const { error } = await supabase.from('library_templates').insert([{
      title, category, content,
      use_count: seedCount(category),
    }])
    if (!error) { setShowForm(false); fetchTemplates() }
  }

  const handleUpdate = async ({ title, category, content }) => {
    const { error } = await supabase.from('library_templates')
      .update({ title, category, content, updated_at: new Date().toISOString() })
      .eq('id', editingTemplate.id)
    if (!error) { setEditingTemplate(null); fetchTemplates() }
  }

  const handleDelete = async (id) => {
    await supabase.from('library_templates').delete().eq('id', id)
    setDeleteConfirm(null)
    fetchTemplates()
  }

  const filtered = templates.filter(t => {
    const matchCat = filterCategory === 'All' || t.category === filterCategory
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
  })

  if (!authorized) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0f172a', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: '#ef4444', fontFamily: "'Open Sans', sans-serif", fontSize: 14, letterSpacing: '0.1em' }}>
          ACCESS DENIED
        </div>
      </div>
    )
  }

  const navSections = [
    { id: 'templates', label: 'Library Templates' },
    // Future sections: { id: 'users', label: 'Users' }, { id: 'announcements', label: 'Announcements' }, etc.
  ]

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', display: 'flex' }}>
      {/* Admin sidebar */}
      <div style={{ width: 220, background: '#1e293b', borderRight: '1px solid #334155', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '24px 20px', borderBottom: '1px solid #334155' }}>
          <div style={{ fontSize: 10, color: '#f1c40f', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 4 }}>Ravenwood</div>
          <div style={{ fontSize: 16, color: '#e2e8f0', fontFamily: "'Cardo', serif" }}>Admin Panel</div>
        </div>
        <nav style={{ padding: '10px 0', flex: 1 }}>
          {navSections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
              width: '100%', textAlign: 'left', padding: '10px 20px',
              background: activeSection === s.id ? '#334155' : 'transparent',
              border: 'none', borderLeft: activeSection === s.id ? '3px solid #f1c40f' : '3px solid transparent',
              color: activeSection === s.id ? '#f1c40f' : '#94a3b8',
              fontSize: 13, fontFamily: "'Nunito Sans', sans-serif", cursor: 'pointer',
            }}>{s.label}</button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #334155' }}>
          <a href="/" style={{ fontSize: 12, color: '#64748b', fontFamily: "'Nunito Sans', sans-serif", textDecoration: 'none' }}>← Back to App</a>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: '24px 36px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 20, color: '#e2e8f0', fontFamily: "'Cardo', serif" }}>Library Templates</div>
          <div style={{ fontSize: 11, color: '#64748b', fontFamily: "'Open Sans', sans-serif', letterSpacing: '0.08em" }}>{templates.length} templates total</div>
        </div>

        <div style={{ padding: '28px 36px' }}>
          {/* Add button */}
          {!showForm && !editingTemplate && (
            <button onClick={() => setShowForm(true)} style={{
              background: '#f1c40f', color: '#0a2342', border: 'none',
              padding: '9px 20px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
              fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer', marginBottom: 24,
            }}>+ Add Template</button>
          )}

          {/* Add form */}
          {showForm && (
            <TemplateForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
          )}

          {/* Edit form */}
          {editingTemplate && (
            <TemplateForm initial={editingTemplate} onSave={handleUpdate} onCancel={() => setEditingTemplate(null)} />
          )}

          {/* Filters */}
          {!showForm && !editingTemplate && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search templates..."
                style={{
                  background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
                  padding: '6px 12px', fontSize: 12, fontFamily: "'Nunito Sans', sans-serif",
                  outline: 'none', width: 200,
                }}
              />
              {['All', ...CATEGORIES].map(c => (
                <button key={c} onClick={() => setFilterCategory(c)} style={{
                  background: filterCategory === c ? '#f1c40f' : 'transparent',
                  color: filterCategory === c ? '#0a2342' : '#94a3b8',
                  border: '1px solid #334155',
                  padding: '5px 12px', fontSize: 11, cursor: 'pointer',
                  fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.06em',
                  fontWeight: filterCategory === c ? 700 : 400,
                }}>{c}</button>
              ))}
            </div>
          )}

          {/* Template list */}
          {loading && <div style={{ color: '#64748b', fontSize: 13, fontFamily: "'Nunito Sans', sans-serif" }}>Loading...</div>}

          {!loading && !showForm && !editingTemplate && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.length === 0 && (
                <div style={{ color: '#64748b', fontSize: 13, fontFamily: "'Nunito Sans', sans-serif", padding: '32px 0' }}>No templates found.</div>
              )}
              {filtered.map(t => (
                <div key={t.id} style={{
                  background: '#1e293b', border: '1px solid #334155',
                  padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#e2e8f0', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 10, color: '#f1c40f', background: '#f1c40f18',
                        padding: '2px 8px', fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em',
                      }}>{t.category}</span>
                      <span style={{ fontSize: 11, color: '#64748b', fontFamily: "'Nunito Sans', sans-serif" }}>{t.use_count} uses</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setEditingTemplate(t)} style={{
                      background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
                      padding: '5px 14px', fontSize: 11, cursor: 'pointer',
                      fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>Edit</button>
                    {deleteConfirm === t.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#ef4444', fontFamily: "'Nunito Sans', sans-serif" }}>Sure?</span>
                        <button onClick={() => handleDelete(t.id)} style={{
                          background: '#ef4444', border: 'none', color: '#fff',
                          padding: '5px 12px', fontSize: 11, cursor: 'pointer',
                          fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
                        }}>Yes</button>
                        <button onClick={() => setDeleteConfirm(null)} style={{
                          background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
                          padding: '5px 12px', fontSize: 11, cursor: 'pointer',
                          fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
                        }}>No</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteConfirm(t.id)} style={{
                        background: 'transparent', border: '1px solid #334155', color: '#ef4444',
                        padding: '5px 14px', fontSize: 11, cursor: 'pointer',
                        fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase',
                      }}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}